function _slicedToArray(r, e) { return _arrayWithHoles(r) || _iterableToArrayLimit(r, e) || _unsupportedIterableToArray(r, e) || _nonIterableRest(); }
function _nonIterableRest() { throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method."); }
function _unsupportedIterableToArray(r, a) { if (r) { if ("string" == typeof r) return _arrayLikeToArray(r, a); var t = {}.toString.call(r).slice(8, -1); return "Object" === t && r.constructor && (t = r.constructor.name), "Map" === t || "Set" === t ? Array.from(r) : "Arguments" === t || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(t) ? _arrayLikeToArray(r, a) : void 0; } }
function _arrayLikeToArray(r, a) { (null == a || a > r.length) && (a = r.length); for (var e = 0, n = Array(a); e < a; e++) n[e] = r[e]; return n; }
function _iterableToArrayLimit(r, l) { var t = null == r ? null : "undefined" != typeof Symbol && r[Symbol.iterator] || r["@@iterator"]; if (null != t) { var e, n, i, u, a = [], f = !0, o = !1; try { if (i = (t = t.call(r)).next, 0 === l) { if (Object(t) !== t) return; f = !1; } else for (; !(f = (e = i.call(t)).done) && (a.push(e.value), a.length !== l); f = !0); } catch (r) { o = !0, n = r; } finally { try { if (!f && null != t["return"] && (u = t["return"](), Object(u) !== u)) return; } finally { if (o) throw n; } } return a; } }
function _arrayWithHoles(r) { if (Array.isArray(r)) return r; }
import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useLocale } from '@adsk/alloy-react-locale';
import Scrollparent from 'scrollparent';
import { createGeocoder, buildAddress } from './utils';
import { GeocoderWrapperStyled } from './styles';
var AutocompleteAddress = function AutocompleteAddress(_ref) {
  var autocompletedAddress = _ref.autocompletedAddress,
    defaultCountry = _ref.defaultCountry,
    disabled = _ref.disabled,
    isInModal = _ref.isInModal,
    mapBoxAccessToken = _ref.mapBoxAccessToken,
    onAddressChange = _ref.onAddressChange,
    onActivateMapboxToken = _ref.onActivateMapboxToken,
    onFailedActivateMapboxToken = _ref.onFailedActivateMapboxToken,
    onResult = _ref.onResult,
    placeholder = _ref.placeholder,
    resetAddressOnDelete = _ref.resetAddressOnDelete;
  var _useLocale = useLocale(function (_ref2) {
      var locale = _ref2.locale;
      return require("./i18n/".concat(locale, ".json"));
    }),
    T = _useLocale.T,
    localeName = _useLocale.localeName;
  var _useState = useState(null),
    _useState2 = _slicedToArray(_useState, 2),
    geocoder = _useState2[0],
    setGeocoder = _useState2[1];
  var _useState3 = useState(null),
    _useState4 = _slicedToArray(_useState3, 2),
    scrollParent = _useState4[0],
    setScrollParent = _useState4[1];
  var _useState5 = useState(0),
    _useState6 = _slicedToArray(_useState5, 2),
    scrollParentOffset = _useState6[0],
    setScrollParentOffset = _useState6[1];
  var geocoderWrapperRef = useRef(null);
  // Keep the onAddressChange latest value
  var latestOnAddressChange = useRef(onAddressChange);
  // Keep the onResult latest value
  var latestOnResult = useRef(onResult);
  // Keep the autocompletedAddress latest value
  var latestAutocompletedAddress = useRef(autocompletedAddress);
  var latestOnActivateMapbox = useRef(onActivateMapboxToken);
  var latestOnFailedActivateMapbox = useRef(onFailedActivateMapboxToken);
  useEffect(function () {
    latestOnAddressChange.current = onAddressChange;
  }, [onAddressChange]);
  useEffect(function () {
    latestOnResult.current = onResult;
  }, [onResult]);
  useEffect(function () {
    latestAutocompletedAddress.current = autocompletedAddress;
  }, [autocompletedAddress]);
  useEffect(function () {
    latestOnActivateMapbox.current = onActivateMapboxToken;
  }, [onActivateMapboxToken]);
  useEffect(function () {
    latestOnFailedActivateMapbox.current = onFailedActivateMapboxToken;
  }, [onFailedActivateMapboxToken]);

  // Find scrolling parent to later adjust suggestions position
  useEffect(function () {
    setScrollParent(Scrollparent(geocoderWrapperRef.current));
  }, []);

  // When scrolling parent is found, bind to scrolling event
  useEffect(function () {
    if (scrollParent === null) return undefined;
    var handleScroll = function handleScroll() {
      setScrollParentOffset(scrollParent.scrollTop);
    };
    scrollParent.addEventListener('scroll', handleScroll);
    return function () {
      scrollParent.removeEventListener('scroll', handleScroll);
    };
  }, [scrollParent]);

  // Geocoder component creation/removal
  useEffect(function () {
    try {
      var newGeocoder = createGeocoder(mapBoxAccessToken, placeholder, localeName);
      newGeocoder._renderNoResults = function _renderNoResults() {
        var errorMessage = "<div class='mapbox-gl-geocoder--error mapbox-gl-geocoder--no-results'>".concat(T.translate('NO_RESULTS'), "</div>");
        // eslint-disable-next-line react/no-this-in-sfc
        this._renderMessage(errorMessage);
      };

      // The following block removes the "Powered by Mapbox" footer introduced in https://github.com/mapbox/mapbox-gl-geocoder/pull/438
      newGeocoder.on('results', function callback() {
        newGeocoder._hideAttribution();
      });
      newGeocoder.addTo('#geocoder_wrapper');
      setGeocoder(newGeocoder);
      latestOnActivateMapbox.current();
    } catch (e) {
      // No action required - mapbox token not active forces the manual input flow
      // eslint-disable-next-line no-console
      console.warn('Warning: Unable to get valid mapbox token, defaulting to ', 'manual address input flow');
      latestOnFailedActivateMapbox.current();
    }
    return function () {
      var geoWrapper = document.getElementById('geocoder_wrapper');
      if (geoWrapper) geoWrapper.innerHTML = '';
    };
  }, [localeName, placeholder, mapBoxAccessToken, defaultCountry, T]);

  // Geocoder event handlers binding/unbinding
  useEffect(function () {
    var handleResult = function handleResult(_ref3) {
      var result = _ref3.result;
      latestOnResult.current(result.place_name);
      buildAddress(result).then(function (address) {
        return latestOnAddressChange.current(address, true);
      })["catch"](function (err) {
        // eslint-disable-next-line no-console
        console.warn('Error building address', err);
      });
    };
    var handleBlur = function handleBlur() {
      var geocoderInput = geocoder._inputEl;
      if (geocoderInput.value !== latestAutocompletedAddress.current) {
        if (geocoderInput.value === '' && resetAddressOnDelete) {
          latestOnResult.current('');
        } else if (latestAutocompletedAddress.current === '') {
          geocoder.setInput(latestAutocompletedAddress.current);
        } else {
          geocoderInput.value = latestAutocompletedAddress.current;
        }
      }
    };
    var bindHandlers = function bindHandlers() {
      geocoder.on('result', handleResult);
      geocoder._inputEl.addEventListener('blur', handleBlur);
    };
    var unbindHandlers = function unbindHandlers() {
      geocoder.off('result', handleResult);
      geocoder._inputEl.removeEventListener('blur', handleBlur);
    };
    if (!geocoder) return undefined;
    bindHandlers();
    return function () {
      unbindHandlers();
    };
  }, [geocoder, resetAddressOnDelete]);

  // Change geocoder input value when autocompleted address changes
  useEffect(function () {
    if (!geocoder) return;
    var geocoderInput = geocoder._inputEl;
    if (autocompletedAddress === '' && geocoderInput.value !== '') {
      geocoder.clear();
      return;
    }
    geocoderInput.value = autocompletedAddress;
  }, [autocompletedAddress, geocoder]);

  // Geocoder disabled property change
  useEffect(function () {
    if (!geocoder) return;
    geocoder._inputEl.disabled = disabled;
  }, [disabled, geocoder]);
  useEffect(function () {
    if (!geocoder || !defaultCountry) return;
    geocoder.setCountries(defaultCountry);
  }, [defaultCountry, geocoder]);
  return /*#__PURE__*/React.createElement(GeocoderWrapperStyled, {
    id: "geocoder_wrapper",
    isInModal: isInModal,
    ref: geocoderWrapperRef,
    suggestionsOffset: scrollParentOffset,
    suggestionsOffsetWidth: geocoderWrapperRef.current ? "".concat(geocoderWrapperRef.current.offsetWidth, "px") : 'auto'
  });
};
AutocompleteAddress.propTypes = {
  autocompletedAddress: PropTypes.string.isRequired,
  defaultCountry: PropTypes.string,
  disabled: PropTypes.bool,
  isInModal: PropTypes.bool,
  mapBoxAccessToken: PropTypes.string.isRequired,
  onActivateMapboxToken: PropTypes.func.isRequired,
  onAddressChange: PropTypes.func.isRequired,
  onFailedActivateMapboxToken: PropTypes.func.isRequired,
  onResult: PropTypes.func.isRequired,
  placeholder: PropTypes.string.isRequired,
  resetAddressOnDelete: PropTypes.bool.isRequired
};
AutocompleteAddress.defaultProps = {
  defaultCountry: '',
  disabled: false,
  isInModal: false
};
export default AutocompleteAddress;
//# sourceMappingURL=component.js.map